import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { NivaAvatarBadge } from "@/components/NivaAvatar";
import { useNiva } from "@/contexts/NivaContext";

/**
 * The shell every buyer ↔ BL chat opens in — a full-height rail down the right
 * edge, the same shape, width token and slide-in as Niva's drawer
 * (ChatbotBubble.tsx), instead of a centered modal that blurred the page.
 *
 * It opens its own gutter (html[data-chat-drawer], index.css) so the page
 * reflows beside it rather than hiding under it. It and Niva share the same
 * spot on screen, so they are mutually exclusive: opening this closes Niva, and
 * opening Niva from the top bar closes this.
 *
 * The header carries Niva's face and "AI Deal Assistant" because she is the
 * one who speaks for the BL side in these threads (her auto-acknowledgment).
 */
export function ChatSideDrawer({
  title,
  subtitle,
  badge,
  onClose,
  children,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Extra header chip, e.g. a deactivated-buyer status. */
  badge?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { open: nivaOpen, closeNiva } = useNiva();

  // Opening a chat takes the rail from Niva…
  useEffect(() => {
    closeNiva();
  }, [closeNiva]);

  // …and Niva opening afterwards takes it back. Only on a closed → open
  // transition: at mount she may still read as open for the render before
  // closeNiva lands, which must not close the chat that just opened.
  const nivaWasOpen = useRef(nivaOpen);
  useEffect(() => {
    if (nivaOpen && !nivaWasOpen.current) onClose();
    nivaWasOpen.current = nivaOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivaOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.chatDrawer = "open";
    return () => {
      delete root.dataset.chatDrawer;
    };
  }, []);

  return (
    <>
      {/* Phones only — there the rail is full-bleed and genuinely modal. */}
      <div
        className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-[2px] sm:hidden"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-label={typeof title === "string" ? title : "Chat"}
        className="niva-drawer-in fixed inset-y-0 right-0 z-[52] flex w-full flex-col border-l border-border/70 bg-background shadow-2xl sm:max-w-[var(--niva-rail-w)]"
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 bg-card/60 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <NivaAvatarBadge className="h-10 w-10" animated />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-sm font-bold leading-tight text-foreground">{title}</p>
                {badge}
              </div>
              <p className="truncate text-[11px] text-muted-foreground">
                {subtitle ?? (
                  <>
                    <span className="font-semibold text-primary">Niva</span> · AI Deal Assistant
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Close chat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </>
  );
}

export default ChatSideDrawer;
